// FilePreviewEditDialog.jsx
// This component is used to display a dialog for editing file information in the file viewer.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { makeStyles } from "@mui/styles";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";

// HSA Imports
import { CaseContext } from "../../cases/components/CaseTableDockSideBar";
import { FileViewerContext } from "../FileViewer";
import FilePreviewList from "./FilePreviewList";

const useStyles = makeStyles(() => ({
  contentContainer: {
    display: "grid",
    gridTemplateRows: "auto 1fr",
    overflow: "hidden",
    height: "100%",
  },
  mainContent: {
    overflowY: "auto",
    height: "100%",
  },
}));

const FilePreviewEditDialog = ({ open, onClose }) => {
  const FV =
    React.useContext(FileViewerContext) || React.useContext(CaseContext);
  const classes = useStyles();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <span>Edit File Information</span>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={FV.showAdditionalName}
                onChange={(e) => FV.setShowAdditionalName(e.target.checked)}
                color="primary"
              />
            }
            label="Show Additional Name"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={FV.showComments}
                onChange={(e) => FV.setShowComments(e.target.checked)}
                color="primary"
              />
            }
            label="Show Comments"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={FV.showLabel}
                onChange={(e) => FV.setShowLabel(e.target.checked)}
                color="primary"
              />
            }
            label="Show Label"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={FV.useAdditionalNameInsteadOfFilename}
                onChange={(e) =>
                  FV.setUseAdditionalNameInsteadOfFilename(e.target.checked)
                }
                color="primary"
              />
            }
            label="Use Additional Name instead of Filename"
          />
          {typeof FV.openInNewWindow === "boolean" &&
            typeof FV.setOpenInNewWindow === "function" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={FV.openInNewWindow}
                    onChange={(e) => FV.setOpenInNewWindow(e.target.checked)}
                    color="primary"
                  />
                }
                label="Open in new Window"
              />
            )}
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.contentContainer}>
          <div className={classes.mainContent}>
            <FilePreviewList editMode={true} />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary" autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FilePreviewEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FilePreviewEditDialog;
